// Hotel booking page (T-09, docs/21 §4): /h/<slug>. One availability request, one hold, one confirm — the
// `direct` channel of the Channel API (no key, rate-limited per address). Instant when the hotel's budget
// covers the stay, otherwise an honest "request" with a deadline. Framework-free so it loads on weak links.
import { t, type Locale } from './i18n.ts';

interface Night {
  night: string;
  instant: boolean;
  requestable: boolean;
  price: { amount: string; currency: string } | null;
}
interface Availability {
  property: {
    slug: string;
    nameAr: string;
    nameEn: string | null;
    currency: string;
    contactPhone: string | null;
  };
  lease: { active: boolean };
  today: string | null;
  from: string;
  to: string;
  roomTypes: {
    id: string;
    code: string;
    nameAr: string;
    nameEn: string | null;
    maxAdults: number;
    maxChildren: number;
    nights: Night[];
  }[];
}
interface Hold {
  holdId: string;
  mode: 'instant' | 'requested';
  expiresAt: string;
  nights: number;
  total: { amount: string; currency: string } | null;
}
interface Booking {
  id: string;
  status: string;
  mode: 'instant' | 'requested';
  cloudConfirmation: string;
  hotelConfirmation: string | null;
  requestedUntil: string | null;
}

const API = '/channels/v1';
const app = document.getElementById('app')!;
let locale: Locale = (localStorage.getItem('syrova.booking.lang') as Locale | null) ?? 'ar';
const slug =
  decodeURIComponent(location.pathname.replace(/^\/h\//, '').replace(/\/$/, '')) ||
  new URLSearchParams(location.search).get('h') ||
  '';

const iso = (d: Date) => d.toISOString().slice(0, 10);
const addDays = (s: string, n: number) =>
  iso(new Date(new Date(`${s}T00:00:00Z`).getTime() + n * 86_400_000));
const fmt = (amount: string, currency: string) =>
  `${Number(amount).toLocaleString(locale === 'ar' ? 'ar-SY' : 'en-GB', { maximumFractionDigits: 2 })} ${currency}`;
const esc = (s: string) =>
  s.replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!,
  );
const field = (fd: FormData, k: string): string => {
  const v = fd.get(k);
  return typeof v === 'string' ? v : '';
};
const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random());

async function api<T>(
  method: 'GET' | 'POST',
  path: string,
  body?: unknown,
  idem?: string,
): Promise<T> {
  const res = await fetch(`${API}${path}`, {
    method,
    headers: {
      ...(body ? { 'content-type': 'application/json' } : {}),
      ...(idem ? { 'idempotency-key': idem } : {}),
    },
    body: body ? JSON.stringify(body) : null,
  });
  const text = await res.text();
  const json = text ? (JSON.parse(text) as unknown) : null;
  if (!res.ok) {
    const err = json as { code?: string; message?: string } | null;
    throw Object.assign(new Error(err?.message ?? res.statusText), {
      code: err?.code ?? 'HTTP_' + res.status,
    });
  }
  return json as T;
}

const state = {
  from: iso(new Date()),
  to: addDays(iso(new Date()), 1),
  adults: 2,
  children: 0,
  typeId: '',
  avail: null as Availability | null,
  hold: null as Hold | null,
  booking: null as Booking | null,
  error: '',
  busy: false,
};

function name(x: { nameAr: string; nameEn: string | null }) {
  return locale === 'en' && x.nameEn ? x.nameEn : x.nameAr;
}

function render() {
  document.documentElement.lang = locale;
  document.documentElement.dir = locale === 'ar' ? 'rtl' : 'ltr';
  app.setAttribute('aria-busy', String(state.busy));
  const a = state.avail;
  const header = a
    ? `<h1>${esc(name(a.property))}</h1><p class="muted">${t(locale, 'title')}${a.lease.active ? '' : ` · ${t(locale, 'offlineHint')}`}</p>`
    : `<h1>${t(locale, 'title')}</h1>`;
  const lang = `<button type="button" class="secondary lang" data-lang>${t(locale, 'lang')}</button>`;
  if (state.booking) {
    const b = state.booking;
    const ok = b.mode === 'instant';
    app.innerHTML = `${lang}${header}<section class="card">
      <h2>${ok ? t(locale, 'confirmedTitle') : t(locale, 'requestedTitle')}</h2>
      ${ok ? '' : `<p class="muted">${t(locale, 'requestedHint')}</p>`}
      <p>${t(locale, 'confirmation')}: <strong class="num">${esc(b.cloudConfirmation)}</strong></p>
      ${b.hotelConfirmation ? `<p>${t(locale, 'hotelConfirmation')}: <span class="num">${esc(b.hotelConfirmation)}</span></p>` : ''}
      ${contact(a)}
      <p><button type="button" class="secondary" data-back>${t(locale, 'back')}</button></p>
    </section><p class="muted">${t(locale, 'powered')}</p>`;
    return;
  }
  const nights = Math.max(
    0,
    Math.round((new Date(state.to).getTime() - new Date(state.from).getTime()) / 86_400_000),
  );
  const form = `<form class="card row" data-search>
    <label>${t(locale, 'arrival')}<input type="date" name="from" value="${state.from}" min="${a?.today ?? ''}" required /></label>
    <label>${t(locale, 'departure')}<input type="date" name="to" value="${state.to}" required /></label>
    <label>${t(locale, 'adults')}<input type="number" name="adults" min="1" max="10" value="${state.adults}" /></label>
    <label>${t(locale, 'children')}<input type="number" name="children" min="0" max="10" value="${state.children}" /></label>
    <button type="submit" ${state.busy ? 'disabled' : ''}>${t(locale, 'search')}</button>
  </form>`;
  const types = a
    ? `<h2>${t(locale, 'roomTypes')} <span class="muted num">${nights} ${t(locale, 'nights')}</span></h2><div class="types">${a.roomTypes
        .map((rt) => {
          const stay = rt.nights.filter((n) => n.night >= state.from && n.night < state.to);
          const instant = stay.length === nights && nights > 0 && stay.every((n) => n.instant);
          const requestable =
            stay.length === nights && nights > 0 && stay.every((n) => n.requestable || n.instant);
          const total =
            stay.every((n) => n.price) && stay.length
              ? stay.reduce((s, n) => s + Number(n.price!.amount), 0)
              : null;
          const fits = state.adults <= rt.maxAdults && state.children <= rt.maxChildren;
          const mode = !fits || !requestable ? 'unavailable' : instant ? 'instant' : 'requestOnly';
          return `<button type="button" class="type" data-type="${rt.id}" aria-pressed="${state.typeId === rt.id}" ${mode === 'unavailable' ? 'disabled' : ''}>
            <span>${esc(name(rt))}<br /><span class="chip ${mode === 'instant' ? 'ok' : mode === 'requestOnly' ? 'warn' : ''}">${t(locale, mode)}</span></span>
            <span class="num">${total !== null ? fmt(String(total), stay[0]!.price!.currency) : ''}<br /><span class="muted">${stay[0]?.price ? `${fmt(stay[0].price.amount, stay[0].price.currency)} ${t(locale, 'perNight')}` : ''}</span></span>
          </button>`;
        })
        .join('')}</div>`
    : '';
  const selected = a?.roomTypes.find((r) => r.id === state.typeId);
  const selStay = selected?.nights.filter((n) => n.night >= state.from && n.night < state.to) ?? [];
  const selInstant = selStay.length === nights && selStay.every((n) => n.instant);
  const guestForm = selected
    ? `<form class="card" data-book>
      <div class="row">
        <label>${t(locale, 'name')}<input name="name" required minlength="2" maxlength="160" /></label>
        <label>${t(locale, 'phone')}<input name="phone" type="tel" required minlength="5" maxlength="40" dir="ltr" /></label>
        <label>${t(locale, 'email')}<input name="email" type="email" maxlength="120" dir="ltr" /></label>
      </div>
      <label>${t(locale, 'notes')}<input name="notes" maxlength="500" /></label>
      <p>${t(locale, 'total')}: <strong class="num">${state.hold?.total ? fmt(state.hold.total.amount, state.hold.total.currency) : ''}</strong></p>
      <button type="submit" ${state.busy ? 'disabled' : ''}>${selInstant ? t(locale, 'book') : t(locale, 'request')}</button>
      ${state.error ? `<p class="error" role="alert">${esc(state.error)}</p>` : ''}
    </form>`
    : state.error
      ? `<p class="error" role="alert">${esc(state.error)}</p>`
      : '';
  app.innerHTML = `${lang}${header}${form}${types}${guestForm}${contact(a)}<p class="muted">${t(locale, 'powered')}</p>`;
}

function contact(a: Availability | null): string {
  if (!a?.property.contactPhone) return '';
  const phone = a.property.contactPhone.replace(/[^\d+]/g, '');
  const text = encodeURIComponent(`${name(a.property)} — ${state.from} → ${state.to}`);
  return `<p><a href="https://wa.me/${phone.replace('+', '')}?text=${text}" rel="noopener">${t(locale, 'whatsapp')}</a> · <a href="tel:${phone}">${t(locale, 'call')}</a></p>`;
}

async function search() {
  state.busy = true;
  state.error = '';
  state.hold = null;
  render();
  try {
    const from = state.from;
    const to = state.to > state.from ? state.to : addDays(state.from, 1);
    state.to = to;
    state.avail = await api<Availability>(
      'GET',
      `/properties/${encodeURIComponent(slug)}/availability?from=${from}&to=${to}`,
    );
    document.title = name(state.avail.property);
  } catch (e) {
    state.avail = null;
    state.error =
      (e as { code?: string }).code === 'NOT_FOUND' ? t(locale, 'notFound') : t(locale, 'error');
  } finally {
    state.busy = false;
    render();
  }
}

async function selectType(id: string) {
  state.typeId = id;
  state.error = '';
  state.busy = true;
  render();
  try {
    // a hold reserves the unit for 10 minutes while the guest types; the price is fixed by the hold
    state.hold = await api<Hold>(
      'POST',
      `/properties/${encodeURIComponent(slug)}/holds`,
      {
        roomTypeId: id,
        arrival: state.from,
        departure: state.to,
        adults: state.adults,
        children: state.children,
        guest: { name: 'pending', phone: '00000' },
      },
      uuid(),
    );
  } catch (e) {
    state.hold = null;
    state.typeId = '';
    state.error =
      (e as { code?: string }).code === 'NOT_AVAILABLE' ? t(locale, 'soldOut') : t(locale, 'error');
  } finally {
    state.busy = false;
    render();
  }
}

async function book(form: HTMLFormElement) {
  const fd = new FormData(form);
  const guest = {
    name: field(fd, 'name'),
    phone: field(fd, 'phone'),
    ...(field(fd, 'email') ? { email: field(fd, 'email') } : {}),
    ...(field(fd, 'notes') ? { notes: field(fd, 'notes') } : {}),
  };
  state.busy = true;
  state.error = '';
  render();
  try {
    // the hold carried placeholder guest data; re-hold with the real guest (same unit, the placeholder expires)
    const hold = await api<Hold>(
      'POST',
      `/properties/${encodeURIComponent(slug)}/holds`,
      {
        roomTypeId: state.typeId,
        arrival: state.from,
        departure: state.to,
        adults: state.adults,
        children: state.children,
        guest,
      },
      uuid(),
    );
    if (state.hold)
      await api('POST', `/bookings/${state.hold.holdId}/cancel`, {}, uuid()).catch(() => undefined);
    state.booking = await api<Booking>('POST', `/holds/${hold.holdId}/confirm`, undefined, uuid());
  } catch (e) {
    const code = (e as { code?: string }).code;
    state.error =
      code === 'HOLD_EXPIRED'
        ? t(locale, 'holdExpired')
        : code === 'NOT_AVAILABLE'
          ? t(locale, 'soldOut')
          : t(locale, 'error');
  } finally {
    state.busy = false;
    render();
  }
}

app.addEventListener('click', (ev) => {
  const el = ev.target as HTMLElement;
  const typeBtn = el.closest<HTMLElement>('[data-type]');
  if (typeBtn?.dataset['type']) void selectType(typeBtn.dataset['type']);
  if (el.closest('[data-lang]')) {
    locale = locale === 'ar' ? 'en' : 'ar';
    localStorage.setItem('syrova.booking.lang', locale);
    render();
  }
  if (el.closest('[data-back]')) {
    state.booking = null;
    state.hold = null;
    state.typeId = '';
    void search();
  }
});
app.addEventListener('submit', (ev) => {
  const form = ev.target as HTMLFormElement;
  ev.preventDefault();
  if (form.matches('[data-search]')) {
    const fd = new FormData(form);
    state.from = field(fd, 'from');
    state.to = field(fd, 'to');
    state.adults = Number(field(fd, 'adults') || 1);
    state.children = Number(field(fd, 'children') || 0);
    state.typeId = '';
    void search();
  } else if (form.matches('[data-book]')) void book(form);
});

if (!slug) {
  app.innerHTML = `<h1>${t(locale, 'title')}</h1><p class="error">${t(locale, 'notFound')}</p>`;
} else void search();
